import type { Session, SupabaseClient } from "@supabase/supabase-js";
import type { AccessDecision } from "../auth/access";
import type { Database, Json } from "../types/database";
import type {
  AuditRepository,
  AuthorizationRepository,
  Profile,
  ProfileRepository,
  Role,
} from "../repositories/interfaces";
import { checkDatabaseReadiness } from "./databaseReadiness";

export type RestoredIdentity = {
  access: AccessDecision;
  profile: Profile | null;
  roles: Role[];
};

export class AuthService {
  constructor(
    private readonly supabase: SupabaseClient<Database>,
    private readonly profileRepository: ProfileRepository,
    private readonly authorizationRepository: AuthorizationRepository,
    private readonly auditRepository: AuditRepository,
  ) {}

  async restoreAuthenticatedIdentity(
    session: Session | null,
  ): Promise<RestoredIdentity> {
    if (!session) {
      return {
        access: this.authorizationRepository.evaluateOwnerAccess(
          null,
          null,
          [],
        ),
        profile: null,
        roles: [],
      };
    }

    const readiness = await checkDatabaseReadiness(this.supabase);

    if (readiness.status !== "ready") {
      throw new Error(readiness.safeMessage ?? "Unable to sign in");
    }

    const profile = await this.profileRepository.getCurrentProfile(session);
    const roles = await this.authorizationRepository.getCurrentRoles(session);
    const access = this.authorizationRepository.evaluateOwnerAccess(
      session,
      profile,
      roles,
    );

    return { access, profile, roles };
  }

  async recordAuthEvent(
    eventType: string,
    eventStatus: string,
    metadata: Json = {},
  ) {
    try {
      await this.auditRepository.recordAuthEvent(
        eventType,
        eventStatus,
        metadata,
      );
    } catch {
      // Authentication telemetry is non-critical and must not block owner access.
    }
  }

  async signInWithGoogle() {
    return this.supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.origin,
        scopes: "email profile",
      },
    });
  }

  async signOutOwner() {
    await this.recordAuthEvent("logout", "success");
    return this.supabase.auth.signOut();
  }
}
